import React, {useMemo} from 'react';

import {bridge} from 'charts/index';
import {Chart} from 'charts/components/Chart';
import SimpleLineSeries from 'charts/components/series/SimpleLineSeries';
import {scaleLinear, scaleTime} from 'd3-scale';

import SimpleVerticalAxis from 'charts/components/axes/SimpleVerticalAxis';
import SimpleBottomAxis from 'charts/components/axes/SimpleBottomAxis';
import {DataPoint} from 'charts/types';

import formatSecondsShort from 'utils/formatSecondsShort';

const margins = {top: 0, left: 0, right: 0, bottom: 0};

export interface ActivityChartDataItem {
  color: string;
  list: Array<DataPoint>;
}

interface ActivityGraphProps {
  from: Date;
  to: Date;
  data: Array<ActivityChartDataItem>;
  height?: number;
  width?: number;
}

export const ActivityGraph: React.FC<ActivityGraphProps> = ({
  from,
  to,
  data,
  height = 140,
  width = 140,
}) => {
  const xScale = useMemo(() => {
    return scaleTime<number, number>()
      .domain([from, to])
      .range([margins.left, width - margins.right]);
  }, [from, to, width]);

  const yScale = useMemo(() => {
    return scaleLinear()
      .domain([
        0,
        Math.max(...data[0].list.map(el => el.value), ...data[1].list.map(el => el.value)),
      ])
      .range([height - margins.bottom, margins.top]);
  }, [data, height]);

  return (
    <Chart svgDimensions={{height, width}} margins={margins}>
      <SimpleVerticalAxis
        amtOfTicks={4}
        margins={margins}
        scale={yScale}
        tickFormat={seconds => formatSecondsShort(seconds as number, false, false, false)}
      />
      <SimpleBottomAxis
        scale={xScale}
        secondPeriodStart={to}
        marginBottom={margins.bottom}
        chartHeight={height}
      />
      {data.map(({color, list}) => {
        return (
          <SimpleLineSeries
            key={color}
            data={bridge(from, to, list)}
            color={color}
            xScale={xScale}
            yScale={yScale}
          />
        );
      })}
    </Chart>
  );
};
